// TODO: This file was created by bulk-decaffeinate.
// Sanity-check the conversion and remove this comment.
/*
 * decaffeinate suggestions:
 * DS207: Consider shorter variations of null checks
 * Full docs: https://github.com/decaffeinate/decaffeinate/blob/master/docs/suggestions.md
 */
import {CodonTranslationTable, NucleotideType} from 'biojs/BioConstants';
import {TranslationGroup} from 'biojs/Sequence';
import {TranslationAttributes} from 'biojs/Translation';
import {AttachPrimerOptions} from 'constants/AttachPrimerConstants';
import {CustomField, PolymerSubtype, Strand} from 'constants/MolbioConstants';
import {SequenceSummary, SequenceType, SequenceServerResponse} from 'constants/SequenceConstants';
import FolderDAOCache from 'file-browser/FolderDAOCache';
import {DiffDetails, EditOp} from 'molbio/biointelligent-links/replace-link/constants';
import * as net from 'util/Net';
import type {BetterJQueryPromise} from 'util/Promises';
import * as TaskRunner from 'util/TaskRunner';

// The builtin JQueryPromise typedef defines the type of the first arg to done callbacks as value?: T rather
// than just value: T, which leads to many unnecessary downstream type assertions.

interface SequenceCreateParams {
  name: string;
  folder: string;
  circular: boolean;
  noteParts?: Array<{text?: string; itemId?: string}>;
  nucleotideType?: NucleotideType;
  type?: SequenceType;
  bases?: string;
  tagSchemaId?: string;
  studyIds?: Array<string>;
}
export function create({
  name,
  folder,
  circular,
  noteParts,
  nucleotideType,
  type,
  tagSchemaId,
  bases = '',
  studyIds = [],
}: SequenceCreateParams): BetterJQueryPromise<SequenceServerResponse> {
  if (circular == null) {
    circular = false;
  }
  return net
    .postJSON({
      url: '/1/api/sequences',
      data: {name, folder, circular, bases, noteParts, nucleotideType, type, tagSchemaId, studyIds},
    })
    .done(() => {
      FolderDAOCache.invalidateFolderContents(folder);
    });
}

export function get(id: string): BetterJQueryPromise<SequenceServerResponse> {
  return net.getJSON(`/1/api/sequences/${id}`);
}

export function getVersion(
  sequenceId: string,
  versionId: number
): BetterJQueryPromise<SequenceServerResponse> {
  return net.getJSON(`/1/api/sequences/${sequenceId}/versions/${versionId}`);
}

export enum TranslationRegionModificationType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
}

interface CreateRegionModification {
  modificationType: TranslationRegionModificationType.CREATE;
  start: number;
  end: number;
  strand: Strand;
}

export interface UpdateRegionModification {
  modificationType: TranslationRegionModificationType.UPDATE;
  id: number;
  strand?: Strand;
}

interface DeleteRegionModification {
  modificationType: TranslationRegionModificationType.DELETE;
  id: number;
}

export type TranslationRegionModification =
  | CreateRegionModification
  | UpdateRegionModification
  | DeleteRegionModification;

export interface TranslationGroupEditableProperties {
  customFields?: Array<CustomField>;
  name?: string;
  notes?: string;
  codonTranslationTable?: CodonTranslationTable;
  coerceStartCodonToMethionine?: boolean;
  color?: string;
}

export interface EditTranslationRegionGroupData {
  regions: Array<TranslationRegionModification>;
  properties?: TranslationGroupEditableProperties;
}

// Translation groups API expects custom fields and translation table keys to be in snake case
interface APITranslationGroupEditableProperties extends Omit<
  TranslationGroupEditableProperties,
  'customFields' | 'codonTranslationTable' | 'coerceStartCodonToMethionine'
> {
  custom_fields?: Array<CustomField>;
  codon_translation_table?: CodonTranslationTable;
  coerce_start_codon_to_methionine?: boolean;
}

interface APIEditTranslationRegionGroupData {
  regions: Array<TranslationRegionModification>;
  properties?: APITranslationGroupEditableProperties;
}

export interface BulkEditTranslationRegionGroupData extends EditTranslationRegionGroupData {
  id: number | null;
}

function reformatTranslationGroupProperties(
  data: EditTranslationRegionGroupData
): APIEditTranslationRegionGroupData {
  const {regions, properties} = data;
  return {
    ...data,
    regions,
    properties:
      properties == null
        ? properties
        : {
            name: properties.name,
            notes: properties.notes,
            custom_fields: properties.customFields,
            codon_translation_table: properties.codonTranslationTable,
            coerce_start_codon_to_methionine: properties.coerceStartCodonToMethionine,
            color: properties.color,
          },
  };
}

export function createTranslationRegionGroup(
  sequenceId: string,
  data: EditTranslationRegionGroupData
): Promise<{translationGroup: TranslationGroup; translationRegions: Array<TranslationAttributes>}> {
  return net.postJSONPromise({
    url: `/1/api/sequences/${sequenceId}/translation-groups`,
    data: reformatTranslationGroupProperties(data),
  });
}

export function editTranslationRegionGroup(
  sequenceId: string,
  translationGroupId: number,
  data: EditTranslationRegionGroupData
): Promise<{translationGroup: TranslationGroup; translationRegions: Array<TranslationAttributes>}> {
  return net.patchJSONPromise({
    url: `/1/api/sequences/${sequenceId}/translation-groups/${translationGroupId}`,
    data: reformatTranslationGroupProperties(data),
  });
}

export function bulkEditTranslationRegionGroups(
  sequenceId: string,
  data: Array<BulkEditTranslationRegionGroupData>
): Promise<{
  updated: Array<{translationGroup: TranslationGroup; translationRegions: Array<TranslationAttributes>}>;
}> {
  return net.putJSONPromise({
    url: `/1/api/sequences/${sequenceId}/translation-groups`,
    data: {modifications: data.map((item) => reformatTranslationGroupProperties(item))},
  });
}

export function batchAutoAnnotate(
  sequenceIds: Array<string>,
  featureLibraryIds: Array<number>
): BetterJQueryPromise<TaskRunner.OngoingTaskResponse> {
  return net.postJSON({
    url: '/1/api/sequences/actions/batch-auto-annotations',
    data: {sequenceIds, featureLibraryIds},
  });
}

export function bulkRemoveAnnotations(sequenceIds: Array<string>): Promise<{sequenceIds: Array<string>}> {
  return net.postJSONPromise({
    url: '/1/api/sequences/actions/bulk-remove-annotations',
    data: {sequenceIds},
  });
}

export function bulkRemoveTranslations(sequenceIds: Array<string>): Promise<{sequenceIds: Array<string>}> {
  return net.postJSONPromise({
    url: '/1/api/sequences/actions/bulk-remove-translations',
    data: {sequenceIds},
  });
}

export function bulkAttachPrimers(
  sequenceIds: Array<string>,
  addPrimerOptions: AttachPrimerOptions
): BetterJQueryPromise<void> {
  return TaskRunner.postJSON({
    url: '/1/api/sequences/bulk-attach-primers',
    data: {sequenceIds, ...addPrimerOptions},
  });
}

export function bulkDetachPrimers(sequenceIds: Array<string>): Promise<{sequenceIds: Array<string>}> {
  return net.postJSONPromise({
    url: '/1/api/sequences/bulk-detach-primers',
    data: {sequenceIds},
  });
}

export function bulkReimportSequences(
  files: Array<{name: string; s3Location: string}>,
  folderId: string,
  nucleotideType?: NucleotideType
): Promise<{sequenceIds: Array<string>}> {
  return TaskRunner.postJSONPromise({
    url: '/1/api/sequences:bulk-reimport',
    data: {files, folderId, nucleotideType},
  });
}

export function bulkUpdateSequences(
  sequenceIds: Array<string>,
  updates: {circular: boolean}
): Promise<Array<SequenceSummary>> {
  // This endpoint accepts more params than circular, but they're not being used. Add in as needed.
  return net.patchJSONPromise({
    url: '/1/api/sequences',
    data: {
      sequence_ids: sequenceIds,
      circular: updates.circular,
    },
  });
}

export function cloneVersion(
  sequenceId: string,
  sequenceVersionId: number,
  folderId: string,
  name: string,
  sequenceAnalysisIds: Array<number> | null = null,
  tagSchemaId: string | null = null
): Promise<{id: string}> {
  return net.postJSONPromise({
    url: `/1/api/sequences/${sequenceId}/versions/${sequenceVersionId}/clone`,
    data: {folder: folderId, name, analyses: sequenceAnalysisIds ?? [], tagSchemaId},
  });
}

export function diffSequences(sequenceAId: string, sequenceBId: string): Promise<DiffDetails> {
  return net.postJSONPromise({
    url: '/1/api/sequences:generate-diff',
    data: {sequenceAId, sequenceBId},
  });
}

export interface ReplaceBiointelligentLinksParams {
  originalComponentId: string;
  newComponentId: string;
  biointelligentLinkType: string;
  containerIds: Array<string>;
  editOps: Array<EditOp>;
  componentPolymerSubtype: PolymerSubtype;
}

export function replaceBiointelligentLinks(params: ReplaceBiointelligentLinksParams): Promise<void> {
  return net.postJSONPromise({
    url: '/1/api/biointelligent-links:replace-links',
    data: params,
  });
}

/** Mirrors benchling.molbio.biointelligent_links.core.biointelligent_link_edit_op_types.BlockingEditReason */
export enum BlockingEditReason {
  INSUFFICIENT_PERMISSIONS = 'INSUFFICIENT_PERMISSIONS',
  CONTAINED_IN_COMPLEX_POLYMER = 'CONTAINED_IN_COMPLEX_POLYMER',
  NESTED_LINKS = 'NESTED_LINKS',
  SELF_OVERLAP = 'SELF_OVERLAP',
  MODIFIED_NUCLEOTIDES_AFFECTED = 'MODIFIED_NUCLEOTIDES_AFFECTED',
}

export interface BlockedEditsResult {
  type: 'blocked';
  reason: BlockingEditReason;
}

export interface EditsEffectsResult {
  type: 'effects';
  overlappingLinkRemovalCount: number;
  featureRemovalCount: number;
  featureMutationCount: number;
}

export type ContainerEditsResult = BlockedEditsResult | EditsEffectsResult;

export interface CheckEditsForContainerEffectsParams {
  originalComponentId: string;
  newComponentId: string;
  biointelligentLinkType: string;
  containerIds: Array<string>;
  editOps: Array<EditOp>;
  componentPolymerSubtype: PolymerSubtype;
}

export function checkEditsForContainerEffects(
  params: CheckEditsForContainerEffectsParams
): Promise<Record<string, ContainerEditsResult>> {
  return net.postJSONPromise({
    url: '/1/api/biointelligent-links:check-edits-for-container-effects',
    data: params,
  });
}
